import React, { useEffect, useState } from 'react';
import { Input } from 'reactstrap';

// The searches are sent when the user stops typing
const SEARCH_DELAY = 400;

/**
 * The searches of the columns of a list: as typed, and as sent (without the empty ones)
 * after the user stopped typing; onApplied is told of a new search, e.g. to go to the first
 * page.
 */
export const useColumnSearch = (
  onApplied?: () => void
): {
  applied: Record<string, string>;
  search: Record<string, string>;
  setValue: (key: string, value: string) => void;
} => {
  const [search, setSearch] = useState<Record<string, string>>({});
  const [applied, setApplied] = useState<Record<string, string>>({});

  useEffect(() => {
    const timeout = setTimeout(() => {
      setApplied(
        Object.fromEntries(
          Object.entries(search).filter(([, value]) => value.trim() !== '')
        )
      );
      onApplied?.();
    }, SEARCH_DELAY);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const setValue = (key: string, value: string) =>
    setSearch((current) => ({ ...current, [key]: value }));

  return { applied, search, setValue };
};

// A click on a search does not open the row
const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * The header of a column of a react-bootstrap-table with its search below its name: a text
 * field, or a select when options are given.
 */
export const columnSearchHeader =
  ({
    onChange,
    options,
    placeholder,
    value,
  }: {
    onChange: (value: string) => void;
    // The choices of a select, the empty one (all) first
    options?: { label: string; value: string }[];
    placeholder: string;
    value: string;
  }) =>
  ({ text }: { text: string }): JSX.Element =>
    (
      <div className="column-search-header">
        <span>{text}</span>
        {options ? (
          <Input
            aria-label={text}
            bsSize="sm"
            onChange={(event) => onChange(event.target.value)}
            onClick={stop}
            type="select"
            value={value}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        ) : (
          <Input
            aria-label={text}
            bsSize="sm"
            onChange={(event) => onChange(event.target.value)}
            onClick={stop}
            placeholder={placeholder}
            type="search"
            value={value}
          />
        )}
      </div>
    );
